/** Fleet / Client Portal — warm cream surfaces + soft aviation accents
 * Page: cream. Cards / fields: ivory & parchment fills.
 * Charts keep a separate colourful palette.
 */

export const COLORS = {
  bg: '#f5f0e8',
  bgMid: '#faf6f0',
  bgDeep: '#ebe4d8',
  bgElevated: '#fffdf9',

  // Card / panel fills — warm ivory on cream
  glass: 'linear-gradient(145deg, rgba(255, 253, 249, 0.96) 0%, rgba(245, 240, 232, 0.92) 100%)',
  glassFlat: 'rgba(255, 253, 249, 0.94)',
  glassLight: 'linear-gradient(145deg, rgba(255, 253, 249, 0.98) 0%, rgba(250, 246, 240, 0.94) 100%)',
  glassLightFlat: 'rgba(255, 253, 249, 0.97)',
  glassHover: 'linear-gradient(145deg, #ffffff 0%, rgba(250, 246, 240, 0.98) 100%)',
  glassHoverFlat: '#ffffff',

  // Field / inset fills — slightly deeper parchment so inputs read as fields
  field: '#efe8dc',
  fieldHover: '#e8e0d2',
  fieldBorder: 'rgba(90, 72, 48, 0.42)',
  fieldBorderStrong: 'rgba(90, 72, 48, 0.58)',

  glassBorder: 'rgba(90, 72, 48, 0.38)',
  glassBorderStrong: 'rgba(90, 72, 48, 0.55)',
  glassHighlight: 'rgba(255, 255, 255, 0.95)',

  // Soft navy — primary fleet accent (reads well on cream)
  orange: '#1d4e78',
  orangeDark: '#163a5c',
  orangeLight: '#3a7ab0',
  orangeGlass: 'rgba(29, 78, 120, 0.1)',

  // Secondary chrome accent
  indigo: '#2a5f8f',
  indigoDark: '#1d4e78',
  indigoLight: '#4a8bc4',
  indigoGlass: 'rgba(42, 95, 143, 0.1)',

  green: '#2f7d57',
  greenDark: '#246346',
  greenLight: '#4caa78',
  greenGlass: 'rgba(47, 125, 87, 0.12)',

  // In-flight — muted teal
  amber: '#2a7a72',
  amberDark: '#1f5f59',
  amberGlass: 'rgba(42, 122, 114, 0.12)',

  red: '#c43c3c',
  redDark: '#a32f2f',
  redGlass: 'rgba(196, 60, 60, 0.1)',

  grey: '#6e6558',
  greyDark: '#524a40',
  greyMuted: '#8a8072',
  greyLight: '#c9c0b2',
  greyGlass: 'rgba(110, 101, 88, 0.1)',

  white: '#ffffff',
  text: '#2c261c',
  textSecondary: '#5a5146',
  textMuted: '#7a7164'
};

/** Warm cream page atmosphere */
export const PAGE_BACKGROUND = `
  radial-gradient(ellipse 80% 60% at 12% 18%, rgba(210, 180, 130, 0.16) 0%, transparent 55%),
  radial-gradient(ellipse 55% 50% at 88% 12%, rgba(29, 78, 120, 0.05) 0%, transparent 50%),
  radial-gradient(ellipse 70% 55% at 50% 100%, rgba(180, 150, 110, 0.1) 0%, transparent 60%),
  linear-gradient(165deg, #faf6f0 0%, #f5f0e8 48%, #efe8dc 100%)
`;

export const PAGE_AMBIENT_SX = {
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '4%',
    left: '2%',
    width: '42%',
    height: '48%',
    borderRadius: 5,
    background: 'linear-gradient(135deg, rgba(210,180,130,0.1) 0%, rgba(255,253,249,0.45) 100%)',
    border: '1px solid rgba(120,100,72,0.06)',
    filter: 'blur(2px)',
    pointerEvents: 'none'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '22%',
    right: '4%',
    width: '34%',
    height: '40%',
    borderRadius: 5,
    background: 'linear-gradient(135deg, rgba(255,253,249,0.55) 0%, rgba(29,78,120,0.04) 100%)',
    border: '1px solid rgba(120,100,72,0.05)',
    filter: 'blur(1px)',
    pointerEvents: 'none'
  }
};

export const panelShellSx = {
  background: 'linear-gradient(160deg, rgba(255, 253, 249, 0.96) 0%, rgba(245, 240, 232, 0.94) 100%)',
  backgroundColor: COLORS.bgElevated,
  backdropFilter: 'blur(28px) saturate(1.08)',
  WebkitBackdropFilter: 'blur(28px) saturate(1.08)',
  border: `1.5px solid ${COLORS.glassBorderStrong}`,
  borderTopColor: COLORS.glassBorderStrong,
  boxShadow: `
    0 20px 48px rgba(44, 38, 28, 0.07),
    0 6px 16px rgba(44, 38, 28, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.95)
  `,
  borderRadius: 5,
  color: COLORS.text
};

export const glassCardSx = {
  background: COLORS.glass,
  backgroundColor: COLORS.glassFlat,
  backdropFilter: 'blur(24px) saturate(1.05)',
  WebkitBackdropFilter: 'blur(24px) saturate(1.05)',
  border: `1.5px solid ${COLORS.glassBorder}`,
  borderTopColor: COLORS.glassBorderStrong,
  boxShadow: `
    0 10px 28px rgba(44, 38, 28, 0.06),
    0 2px 8px rgba(44, 38, 28, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.95)
  `,
  borderRadius: 4,
  color: COLORS.text
};

export const glassCardHoverSx = {
  transition: 'transform 0.28s ease, box-shadow 0.28s ease, background 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    background: COLORS.glassHover,
    backgroundColor: COLORS.glassHoverFlat,
    boxShadow: `
      0 16px 40px rgba(44, 38, 28, 0.09),
      0 4px 12px rgba(44, 38, 28, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.98)
    `
  }
};

export const coloredCardSx = (gradient) => ({
  background: gradient,
  color: COLORS.white,
  borderRadius: 4,
  boxShadow: '0 8px 24px rgba(44, 38, 28, 0.12)',
  border: '1px solid rgba(255, 255, 255, 0.35)',
  position: 'relative',
  overflow: 'hidden'
});

export const coloredCardHoverSx = {
  transition: 'transform 0.22s ease, box-shadow 0.22s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 14px 32px rgba(44, 38, 28, 0.15)'
  }
};

export const coloredCardOrbSx = {
  position: 'absolute',
  width: 88,
  height: 88,
  borderRadius: '50%',
  bgcolor: 'rgba(255,255,255,0.18)',
  top: -24,
  right: -16,
  pointerEvents: 'none'
};

export const coloredCardInsetSx = {
  background: 'rgba(255, 255, 255, 0.22)',
  border: '1px solid rgba(255, 255, 255, 0.35)',
  borderRadius: 2.5,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)'
};

export const OPERATIONAL_STATUSES = new Set(['AVAILABLE', 'IN_FLIGHT']);

export const isAircraftOperational = (status) => OPERATIONAL_STATUSES.has(status);

const statusGlassCard = (accent) => ({
  background: `linear-gradient(160deg, ${accent}14 0%, rgba(255, 253, 249, 0.96) 55%, rgba(245, 240, 232, 0.96) 100%)`,
  backgroundColor: COLORS.bgElevated,
  backdropFilter: 'blur(22px) saturate(1.05)',
  WebkitBackdropFilter: 'blur(22px) saturate(1.05)',
  border: `1.5px solid ${accent}70`,
  borderTopColor: `${accent}90`,
  boxShadow: `
    0 0 0 1px ${accent}22,
    0 10px 28px ${accent}16,
    0 4px 12px rgba(44, 38, 28, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.95)
  `,
  borderRadius: 4,
  color: COLORS.text,
  position: 'relative',
  overflow: 'hidden'
});

export const glassyGreenCardSx = statusGlassCard(COLORS.green);
export const glassyAmberCardSx = statusGlassCard(COLORS.amber);
export const glassyOrangeCardSx = statusGlassCard(COLORS.orange);
export const glassyRedCardSx = statusGlassCard(COLORS.red);

export const glassyStatusHoverSx = {
  transition: 'transform 0.22s ease, box-shadow 0.22s ease',
  '&:hover': {
    transform: 'translateY(-3px)'
  }
};

/** Stacked depth layers behind aircraft cards */
export const stackedCardWrapSx = (accent) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    left: 10,
    right: 10,
    height: '100%',
    borderRadius: 4,
    pointerEvents: 'none',
    zIndex: 0
  },
  '&::before': {
    top: 10,
    background: `linear-gradient(160deg, ${accent}10 0%, rgba(245,240,232,0.75) 100%)`,
    border: `1.5px solid ${accent}40`,
    boxShadow: `0 8px 24px ${accent}14`
  },
  '&::after': {
    top: 5,
    background: `linear-gradient(160deg, ${accent}12 0%, rgba(255,253,249,0.85) 100%)`,
    border: `1.5px solid ${accent}50`
  }
});

export const getAircraftGlassCardSx = (status) => {
  switch (status) {
    case 'AVAILABLE':
      return glassyGreenCardSx;
    case 'IN_FLIGHT':
      return glassyAmberCardSx;
    case 'MAINTENANCE':
    case 'GROUNDED':
      return glassyRedCardSx;
    case 'INSPECTION':
      return glassyOrangeCardSx;
    default:
      return glassyOrangeCardSx;
  }
};

export const getAircraftStatusAccent = (status) =>
  AIRCRAFT_STATUS_ACCENT[status] || AIRCRAFT_STATUS_ACCENT.DEFAULT;

export const surfaceInsetSx = {
  background: COLORS.field,
  backgroundColor: COLORS.field,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1.5px solid ${COLORS.fieldBorder}`,
  borderRadius: 3,
  boxShadow: 'inset 0 1px 2px rgba(44,38,28,0.04)'
};

export const accordionSummarySx = {
  ...surfaceInsetSx,
  borderRadius: 0,
  borderLeft: `3px solid ${COLORS.indigo}`,
  borderTop: 'none',
  borderRight: 'none',
  borderBottom: `1.5px solid ${COLORS.glassBorder}`
};

export const accentBarSx = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 1.5,
  '&::before': {
    content: '""',
    width: 3,
    borderRadius: 2,
    bgcolor: COLORS.indigo,
    flexShrink: 0,
    alignSelf: 'stretch'
  }
};

export const sectionTitleSx = {
  color: COLORS.text,
  fontWeight: 800,
  display: 'flex',
  gap: 1.5,
  alignItems: 'stretch',
  '&::before': {
    content: '""',
    width: 3,
    borderRadius: 2,
    bgcolor: COLORS.indigo,
    flexShrink: 0
  }
};

export const pillPrimarySx = {
  borderRadius: 999,
  px: 2.5,
  py: 0.75,
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: COLORS.orange,
  color: COLORS.white,
  boxShadow: '0 4px 16px rgba(29, 78, 120, 0.24)',
  '&:hover': { bgcolor: COLORS.orangeDark, boxShadow: '0 6px 20px rgba(22, 58, 92, 0.3)' }
};

export const pillSecondarySx = {
  borderRadius: 999,
  px: 2.5,
  py: 0.75,
  textTransform: 'none',
  fontWeight: 600,
  background: COLORS.bgElevated,
  backgroundColor: COLORS.bgElevated,
  color: COLORS.text,
  border: `1.5px solid ${COLORS.glassBorderStrong}`,
  boxShadow: 'none',
  '&:hover': {
    background: COLORS.field,
    backgroundColor: COLORS.field,
    borderColor: COLORS.fieldBorderStrong
  }
};

export const pillDangerSx = {
  borderRadius: 999,
  px: 2.5,
  py: 0.75,
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: COLORS.red,
  color: COLORS.white,
  boxShadow: '0 4px 16px rgba(220, 38, 38, 0.25)',
  '&:hover': { bgcolor: COLORS.redDark }
};

export const floatingIconSx = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  bgcolor: COLORS.orange,
  color: COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 6px 16px rgba(29, 78, 120, 0.24)',
  border: `1.5px solid ${COLORS.glassBorderStrong}`
};

export const tableHeaderSx = {
  background: COLORS.field,
  backgroundColor: COLORS.field,
  '& .MuiTableCell-root': { color: COLORS.textMuted, fontWeight: 700, borderColor: COLORS.glassBorder }
};

export const tableRowSx = {
  '& .MuiTableCell-root': { color: COLORS.textSecondary, borderColor: COLORS.glassBorder },
  '&:hover': { background: COLORS.fieldHover, backgroundColor: COLORS.fieldHover }
};

export const overdueRowSx = { bgcolor: COLORS.redGlass };

export const tooltipPaperSx = {
  p: 1.5,
  border: `1.5px solid ${COLORS.glassBorderStrong}`,
  background: COLORS.glassHover,
  backgroundColor: COLORS.glassHoverFlat,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  color: COLORS.text,
  boxShadow: '0 8px 24px rgba(44,38,28,0.1)'
};

export const alertInfoSx = {
  borderRadius: 3,
  ...glassCardSx,
  color: COLORS.textSecondary,
  '& .MuiAlert-icon': { color: COLORS.indigo }
};

export const alertErrorSx = {
  borderRadius: 3,
  bgcolor: COLORS.redGlass,
  color: COLORS.text,
  border: `1px solid rgba(220, 38, 38, 0.3)`,
  '& .MuiAlert-icon': { color: COLORS.red }
};

export const KPI_THEMES = [
  { accent: COLORS.green, label: 'Available', bg: COLORS.greenGlass },
  { accent: COLORS.amber, label: 'In Flight', bg: COLORS.amberGlass },
  { accent: COLORS.red, label: 'Maintenance', bg: COLORS.redGlass },
  { accent: COLORS.indigo, label: 'Grounded', bg: COLORS.indigoGlass },
  { accent: COLORS.grey, label: 'Inspection', bg: COLORS.greyGlass },
  { accent: COLORS.orangeLight, label: 'Active Charters', bg: COLORS.orangeGlass }
];

export const AIRCRAFT_STATUS_GRADIENTS = {
  AVAILABLE: COLORS.glassFlat,
  IN_FLIGHT: COLORS.glassFlat,
  MAINTENANCE: COLORS.glassLightFlat,
  INSPECTION: COLORS.glassLightFlat,
  GROUNDED: COLORS.redGlass,
  DEFAULT: COLORS.glassFlat
};

export const AIRCRAFT_STATUS_ACCENT = {
  AVAILABLE: COLORS.green,
  IN_FLIGHT: COLORS.amber,
  MAINTENANCE: COLORS.red,
  INSPECTION: COLORS.grey,
  GROUNDED: COLORS.indigo,
  DEFAULT: COLORS.greyMuted
};

export const HERO_GRADIENTS = {
  AVAILABLE: 'linear-gradient(135deg, #4caa78 0%, #2f7d57 45%, #246346 100%)',
  IN_FLIGHT: 'linear-gradient(135deg, #3d9a90 0%, #2a7a72 50%, #1f5f59 100%)',
  MAINTENANCE: 'linear-gradient(135deg, #d96a6a 0%, #c43c3c 50%, #a32f2f 100%)',
  INSPECTION: 'linear-gradient(135deg, #a89f90 0%, #6e6558 50%, #524a40 100%)',
  GROUNDED: 'linear-gradient(135deg, #4a8bc4 0%, #2a5f8f 50%, #1d4e78 100%)',
  DEFAULT: 'linear-gradient(135deg, #3a7ab0 0%, #1d4e78 40%, #163a5c 100%)'
};

/** Colourful palette — graphs only */
export const CHART_COLORS = [
  '#1d4e78', '#2a7a72', '#6366f1', '#2f7d57', '#4a8bc4',
  '#818cf8', '#3a7ab0', '#c43c3c', '#4caa78', '#2a5f8f'
];

export const TAB_COLORS = [
  COLORS.indigo,
  COLORS.indigo,
  COLORS.indigo,
  COLORS.indigo,
  COLORS.indigo
];

export const CHARTER_SECTION_THEMES = {
  active: { bg: COLORS.indigoGlass, accent: COLORS.indigo, chip: 'default' },
  upcoming: { bg: COLORS.greyGlass, accent: COLORS.greyDark, chip: 'default' },
  completed: { bg: COLORS.glassLightFlat, accent: COLORS.grey, chip: 'default' }
};

export const STAT_CARD_STYLES = {
  indigo: COLORS.orange,
  indigoDark: COLORS.orangeDark,
  indigoLight: COLORS.orangeLight,
  red: COLORS.red,
  redDark: COLORS.redDark,
  grey: COLORS.greyDark,
  greyLight: COLORS.greyMuted
};

export const chartPaperSx = {
  ...glassCardSx,
  p: 3,
  minHeight: 460
};

export const axisLabelStyle = {
  fill: COLORS.textMuted,
  fontSize: 12,
  fontWeight: 600,
  textAnchor: 'middle'
};

export const makeXAxisLabel = (text, offset = 0) => ({
  value: text,
  position: 'insideBottom',
  offset,
  dy: 14,
  style: axisLabelStyle
});

export const makeYAxisLabel = (text, offset = 0) => ({
  value: text,
  position: 'insideLeft',
  offset,
  dx: -8,
  dy: 0,
  style: { ...axisLabelStyle, textAnchor: 'middle' },
  angle: -90
});

export const makeYAxisLabelRight = (text, offset = 0) => ({
  value: text,
  angle: 90,
  position: 'insideRight',
  offset,
  dx: 8,
  style: { ...axisLabelStyle, textAnchor: 'middle' }
});

export const CHART_MARGINS = {
  default: { top: 16, right: 24, left: 20, bottom: 40 },
  dualY: { top: 16, right: 56, left: 24, bottom: 40 },
  vertical: { top: 16, right: 24, left: 12, bottom: 40 },
  compact: { top: 16, right: 20, left: 20, bottom: 40 }
};

export const getStatusGradient = (status, map = AIRCRAFT_STATUS_GRADIENTS) =>
  map[status] || map.DEFAULT;

export const getStatusAccent = (status) =>
  AIRCRAFT_STATUS_ACCENT[status] || AIRCRAFT_STATUS_ACCENT.DEFAULT;
